
// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Form, Row, Space, Typography, Modal, Spin, Collapse, Divider, Tree, Tooltip, Tabs, Select, message } from 'antd';
import TopicModellerClusterChart from '../Charts/TopicModellerClusterChart';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { truncate } from '../../utils/string/truncate';
import { useCreateTopicModellingJobMutation, useFetchTopicModellingJobQuery, useLazyFetchTopicModellingJobTopicsQuery } from '../../store/topicModellingJobs/topicModellingJobsApi';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { TOPIC_MODELLING_JOB_STATUSES } from '../../constants/main';
import { TOPIC_MODELLING_MAX_CHARACTERS_LIMIT } from '../../constants/env';
import { INTEGRATION_TYPE } from '../../constants/integrations';
import { useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';

// Constants
const MAX_CORPUS_ITEM_CHARACTER_DISPLAY_LIMIT = 80;
const POLLING_INTERVAL = 3000;
const MAX_DEPTH_LIMIT = 3;
const CORPUS_ITEMS_DEPTH_LIMIT = 1000;

const { Panel } = Collapse;

const TABS = {
  TREE_VIEW: 'tableView',
  CLUSTER_VIEW: 'clusterView'
};

// Function to count occurrences
const countOccurrences = (item, array) => {
  return array.reduce((count, obj) => {
    return obj.question.includes(item) ? count + obj.occurrences : count;
  }, 0);
};

// Function to transform corpus items
const transformCorpusItems = (corpus, data) => {
  if (!corpus || corpus.length === 0) {
    return null;
  }
  return corpus.map(item => ({
    label: item,
    occurrences: countOccurrences(item, data)
  }));
};

// Recursive function to collect all corpus items
const collectAllCorpusItems = (topics, data) => {
  let allCorpusItems = [];
  const recursiveCollect = (topic) => {
    if (topic.corpus) {
      allCorpusItems = allCorpusItems.concat(transformCorpusItems(topic.corpus, data));
    }
    if (topic.topics && topic.topics.length > 0) {
      topic.topics.forEach(subTopic => recursiveCollect(subTopic));
    }
  };
  topics.forEach(topic => recursiveCollect(topic));
  return allCorpusItems;
};

// Function to aggregate and transform the topic data
const aggregateAndTransformTopics = (data, completeData) => {
  if (!data || !data.length) {
    return [];
  }

  return data.map(item => {
    const transformedTopics = item.topics ? aggregateAndTransformTopics(item.topics, completeData) : [];
    const allCorpusItems = collectAllCorpusItems([item], completeData);

    return {
      ...item,
      topics: transformedTopics,
      corpus: allCorpusItems
    };
  });
};

const TopicModellerForm = ({
  corpus = [],
  entityName = 'Item',
  renderCorpusItemDisplay = (item) => {
    return truncate(item, MAX_CORPUS_ITEM_CHARACTER_DISPLAY_LIMIT, '...');
  },
  onCorpusItemClick = item => {},
  headerTitle = '',
  completeData = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const { t } = useTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [topicsData, setTopicsData] = useState([]); // State to hold the mock data
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [currentJobId, setCurrentJobId] = useState('');
  const [corpusItemsToBeProcessed, setCorpusItemsToBeProcessed] = useState([]);
  const [currentViewTab, setCurrentViewTab] = useState(TABS.TREE_VIEW);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const { data: integrations, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { pageSize: 1000, type: INTEGRATION_TYPE.OPEN_AI_LLM } }, { skip: !orgId });
  const [createJob, { isLoading: isCreatingJob }] = useCreateTopicModellingJobMutation();
  const { data: job } = useFetchTopicModellingJobQuery(
    { jobId: currentJobId },
    {
      skip: !currentJobId && !isPolling,
      pollingInterval: currentJobId && isPolling ? POLLING_INTERVAL : 0
    });

  const [fetchJobTopics] = useLazyFetchTopicModellingJobTopicsQuery();

  const { showModalConfirm } = useModalConfirm(isPolling && isModalVisible);

  useEffect(() => {
    if(isEmptyArray(corpus)) {
      navigateBack();
    } else {
      setCorpusItemsToBeProcessed(corpus);
    }
  }, [corpus?.length]);

  useEffect(() => {
    if (job?.status === TOPIC_MODELLING_JOB_STATUSES.COMPLETED) {

      (async () => {
        try {
          const topics = await fetchJobTopics({ jobId: currentJobId }).unwrap();
          setTopicsData(topics ?? []); // Populate with the fetched topics data
          setIsPolling(false);
          setIsModalVisible(false);
        } catch (error) {
          setIsPolling(false);
          setIsModalVisible(false);
          console.error('Error fetching job topics:', error);
        }
      }) ();
    }

    if(job?.status === TOPIC_MODELLING_JOB_STATUSES.FAILED) {
      setTopicsData([]); // Populate with the fetched topics data
      setIsPolling(false);
      setIsModalVisible(false);
      setCurrentJobId('');
    }
  }, [job]);

  /**
   * Calculate corpus total character count
   * Purpose is to compare against TOPIC_MODELLER_MAX_CHARACTER_LIMIT
   * @param {String[]} corpus
   * @returns {number} total Character count
   */
  const calculateCorpusCharactersSum = (corpus = []) => {
    let charactersCount = 0;
    if(corpus?.length > 0) {
      corpus.forEach((item) => {
        charactersCount += item?.length;
      });
    }
    return charactersCount;
  };

  // Function to handle the button click
  const handleCreateTopics = async () => {
    if (!selectedIntegration) {
      message.error(t('Please select an integration to proceed.'));
      return;
    }

    try {
      const jobCreateResponse = await createJob({
        data: {
          corpus: corpusItemsToBeProcessed,
          depth: corpusItemsToBeProcessed?.length >= CORPUS_ITEMS_DEPTH_LIMIT ? 2 : 1,
          openai_api_key: selectedIntegration?.apiKey
        }
      }).unwrap();

      if(jobCreateResponse.status === TOPIC_MODELLING_JOB_STATUSES.NEW || jobCreateResponse.status === TOPIC_MODELLING_JOB_STATUSES.RUNNING) {
        setCurrentJobId(jobCreateResponse?.id);
        setIsPolling(true);
        setIsModalVisible(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleExpandedKeys = (key) => {
    setExpandedKeys(prevKeys => {
      if (prevKeys.includes(key)) {
        return prevKeys.filter(k => k !== key);
      } else {
        return [...prevKeys, key];
      }
    });
  };

  const handleModalCancel = () => {
    showModalConfirm({
      onConfirm: () => {
        setIsModalVisible(false);
        setIsPolling(false);
        setTopicsData([]);
        setCurrentJobId('');
      },
      okText: t('Abort Process'),
      cancelText: t('Continue'),
      title: t('Abort Process?'),
      content: t('The process will be canceled. Are you sure you want to abort?')
    });
  };

  const navigateBack = () => {
    setTopicsData([]);
    setCurrentJobId('');
    setIsPolling(false);
    setIsModalVisible(false);

    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.delete('topicModeller');
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  /**
   *  It returns an aggregated array of corpus items based on the children
   * @param {Object} topic
   * @param {Object[]} topicChildren
   * @returns {String[]}
   */
  const handleBottomUpCorpusAggregation = (topic, topicChildren) => {
    if(!topic) {
      return;
    }

    let currentCorpus = topic?.corpus ? [...topic.corpus] : [];

    if(topicChildren) {
      topicChildren?.forEach(child => {
        if(child.corpus) {
          currentCorpus = currentCorpus.concat(child.corpus);
        }
      });
    }

    return currentCorpus;
  };

  const aggregateTopicCorpus = (data) => {
    if (!data || !data.length) {
      return [];
    }

    return data.map(item => {
      const children = item?.topics ? aggregateTopicCorpus(item.topics) : [];
      const aggregatedCorpus = handleBottomUpCorpusAggregation(item, children);

      return {
        ...item,
        topics: children,
        corpus: aggregatedCorpus.length > 0 ? aggregatedCorpus : null
      };
    });
  };

  /**
   * Recursive function to build depth and the topics based on depth 0 and it's children
   * @param data
   * @param parentKey
   * @param depth
   * @returns {*|*[]}
   */
  const transformDataToTree = (data, parentKey = '', depth = 0) => {
    if (depth >= MAX_DEPTH_LIMIT) {
      return [];
    }

    return data.map((item, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      const children = item?.topics && item?.topics?.length > 0 ? transformDataToTree(item?.topics, key, depth + 1) : null;

      return {
        title: renderTopicCollapse(item, key, depth),
        key: key,
        children: children,
        className: parentKey ? `custom-tree-line` : ''
      };
    });
  };

  const renderTopicHeader = (name, missedTotal) => {
    return (
      <Row className="w-100">
        <Col xs={20}>
          <Typography.Text className="fw-bold fs-lg">
            {name}
          </Typography.Text>
        </Col>
        <Col xs={24} className="pt-2">
          <Typography.Text className="text-muted">
            {`${missedTotal || 0} ${entityName}`}
          </Typography.Text>
        </Col>
      </Row>
    );
  };

  const renderTopicCollapse = (topic, key, currentDepth) => {
    const isExpanded = expandedKeys.includes(key);
    return (
      <Fragment>
        <Row>
          <Col xs={24}>
            <Collapse
              className="topic-modeller-collapse scrollbar-custom"
              key={`${topic.name}${currentDepth}`}
              bordered
              expandIcon={({ isActive }) => <ArrowRightIcon rotate={isActive ? 90 : 0} className="fs-base mt-1" />}
            >
              {renderTopicPanel(topic)}
            </Collapse>
          </Col>
          <Col>
            {
              (currentDepth + 1) !== MAX_DEPTH_LIMIT
                  && topic?.topics?.length > 0
                  && <Typography.Text
                    className="mb-0 text-link view-subtopics"
                    onClick={() => handleExpandedKeys(key)}>
                    <ArrowRightIcon className={isExpanded ? 'rotated-90 mr-1 text-link' : 'rotated-0 mr-1 text-link'}/>
                    {t(`View ${topic?.topics?.length} subtopics`)}
                  </Typography.Text>
            }
          </Col>
        </Row>
      </Fragment>
    );
  };

  const renderTopicPanel = (topic) => {
    return (
      <Fragment>
        <Panel
          header={renderTopicHeader(topic?.name, topic?.corpus?.length)}
          key={`${topic?.name}${topic?.corpus?.length}`
          }>
          { topic?.corpus?.map((item, index) => renderCorpusItem(item)) }
        </Panel>
      </Fragment>
    );
  };

  const renderCorpusItem = (item, index) => {
    return (
      <Fragment key={`${item}${index}`}>
        <Row className="py-3 pl-9 w-100 question-item-row">
          <Col xs={24}>
            <Tooltip
              title={item?.length > MAX_CORPUS_ITEM_CHARACTER_DISPLAY_LIMIT && item}
              placement="right"
            >
              <Typography.Text onClick={() => onCorpusItemClick(item)} className="fs-base w-100">
                {renderCorpusItemDisplay(item)}
              </Typography.Text>
            </Tooltip>
          </Col>
        </Row>
        <Divider className="w-100 p-0 m-0"/>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Space wrap align="center" size={16} className="mb-4 w-100 justify-content-between">
        <Space size={12} align="center">
          <Button
            ghost
            size="large"
            icon={<ArrowLeftIcon />}
            className="shadow border-0"
            onClick={navigateBack}
          />

          <Form.Item
            noStyle
          >
            <h3 className="mb-0">
              <Space>
                {headerTitle}
              </Space>
            </h3>
          </Form.Item>
        </Space>
      </Space>

      <Card className="mb-4" hidden={topicsData?.length > 0}>
        <Row className="mb-7">
          <Col xs={24}>
            <Typography.Text className="fw-bold fs-lg">
              {corpusItemsToBeProcessed?.length ?? 0} {t(`${entityName} Selected`)}
            </Typography.Text>
          </Col>

          <Col xs={24}>
            <Typography.Text className="text-muted">
              {t('Select an OpenAI integration to use for extracting topics.')}
            </Typography.Text>
          </Col>

          <Col xs={24} className="mt-3">
            <Select
              size="large"
              className="max-field-width w-100"
              placeholder={t('Select Integration')}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              loading={isFetchingIntegrations}
              disabled={isFetchingIntegrations}
              onChange={(value) => {
                setSelectedIntegration(integrations?.items.find(integration => integration.id === value));
              }}
              value={selectedIntegration?.id}
              notFoundContent={isFetchingIntegrations ? <Spin size="small" /> : null}
            >
              {
                (integrations?.items || []).map(integration => (
                  <Select.Option key={integration.id} value={integration.id}>
                    {integration.name}
                  </Select.Option>
                ))
              }
            </Select>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Typography.Text className="text-muted">
              {t(`Click "Start" to begin the topic analysis for the selected ${entityName}.`)}
            </Typography.Text>
          </Col>

          <Col xs={3} className="mt-3">
            <Tooltip
              title={(!!TOPIC_MODELLING_MAX_CHARACTERS_LIMIT && calculateCorpusCharactersSum(corpusItemsToBeProcessed) > TOPIC_MODELLING_MAX_CHARACTERS_LIMIT) ? `Total ${entityName} characters exceed max character limit of ${TOPIC_MODELLING_MAX_CHARACTERS_LIMIT}` : null}
            >
              <Button
                type="primary"
                size="large"
                onClick={handleCreateTopics}
                disabled={corpusItemsToBeProcessed?.length === 0 || (!!TOPIC_MODELLING_MAX_CHARACTERS_LIMIT && calculateCorpusCharactersSum(corpusItemsToBeProcessed) > TOPIC_MODELLING_MAX_CHARACTERS_LIMIT)}
                loading={isCreatingJob}
              >
                {t('Start')}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Card>

      <Modal
        open={isModalVisible}
        footer={null}
        closable={true}
        centered
        onCancel={() => {
          handleModalCancel();
        }}

      >
        <Row className="text-center">
          <Col xs={24}>
            <Typography.Text className="fw-bold fs-lg">
              {t('Extracting Topics')}
            </Typography.Text>
          </Col>
          <Col xs={24}>
            <Row className="mt-4">
              <Col xs={24}>
                <Typography>
                  {t('Please wait...')}
                </Typography>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-4" justify="center" align="center">
          <Spin spinning={isPolling} size="default" />
        </Row>
      </Modal>

      <Row hidden={topicsData?.length === 0}>
        <Col xs={24}>
          <Form.Item
            noStyle
          >
            <Tabs
              activeKey={currentViewTab}
              defaultActiveKey={TABS.TREE_VIEW}
              onTabClick={(key) => setCurrentViewTab(key)}
              items={[
                TABS.TREE_VIEW && {
                  key: TABS.TREE_VIEW,
                  label: t('Topics Tree')
                },
                TABS.CLUSTER_VIEW && {
                  key: TABS.CLUSTER_VIEW,
                  label: t('Clustered Topics')
                }
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row hidden={topicsData?.length === 0 || currentViewTab !== TABS.TREE_VIEW} className="mt-4">
        <Col xs={24}>
          <Tree
            showLine
            expandedKeys={expandedKeys}
            treeData={transformDataToTree(aggregateTopicCorpus(topicsData))}
            className="topic-modeller-tree"
            showIcon={false}
          />
        </Col>
      </Row>
      <Row hidden={topicsData?.length === 0 || currentViewTab !== TABS.CLUSTER_VIEW} className="mt-4" justify={'center'}>
        {
          topicsData?.length > 0 && (
            <TopicModellerClusterChart isActive={currentViewTab === TABS.CLUSTER_VIEW} topicsData={aggregateAndTransformTopics(topicsData, completeData)} showOccurences={entityName === 'Missed Questions'}/>
          )
        }
      </Row>
    </Fragment>
  );
};

TopicModellerForm.propTypes = {
  corpus: PropTypes.arrayOf(PropTypes.string).isRequired,
  entityName: PropTypes.string.isRequired,
  renderCorpusItemDisplay: PropTypes.func,
  onCorpusItemClick: PropTypes.func,
  headerTitle: PropTypes.string,
  completeData: PropTypes.array,
  botPlugin: PropTypes.object
};
export default TopicModellerForm;
